import { useEffect, useRef, useState } from "react";
import { Play, Pause, SkipBack, SkipForward, SpeakerHigh, SpeakerSlash, ClosedCaptioning, CursorClick } from "@phosphor-icons/react";
import { useLaunchLiveTour, TOUR_ROLES } from "./LiveTour";
import { useAuth } from "../contexts/AuthContext";

/* Narrated slideshow at the top of a guide: real screenshots + a short voice-over per slide.
   Audio drives the slide timer (next slide when the clip ends); without audio each slide holds for ~7 s. */
const HOLD_MS = 7000;

export function KbWalkthrough({ walkthrough, slug, compact = false, autoPlay = false, onFinished }) {
  const slides = walkthrough?.slides || [];
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(autoPlay);
  const { user } = useAuth();
  const launchLive = useLaunchLiveTour();
  const liveTour = walkthrough?.live_tour && TOUR_ROLES.includes(user?.role) ? walkthrough.live_tour : null;
  const [muted, setMuted] = useState(false);
  const [captions, setCaptions] = useState(true);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);
  const timerRef = useRef(null);
  const slide = slides[i];
  const narrated = !!walkthrough?.narrated;

  const clearTimer = () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };

  const goTo = (n, autoplay = playing) => {
    clearTimer();
    const next = Math.max(0, Math.min(slides.length - 1, n));
    setI(next);
    setProgress(0);
    if (!autoplay) setPlaying(false);
  };

  const advance = () => {
    if (i >= slides.length - 1) { setPlaying(false); setProgress(100); onFinished?.(); return; }
    goTo(i + 1, true);
  };

  // Audio-driven playback
  useEffect(() => {
    const a = audioRef.current;
    if (!slide) return undefined;
    clearTimer();
    if (slide.audio && a) {
      a.src = slide.audio;
      a.muted = muted;
      a.load();
      if (playing) a.play().catch(() => {});
      return undefined;
    }
    if (!playing) return undefined;
    const started = Date.now();
    timerRef.current = setInterval(() => {
      const pct = Math.min(100, ((Date.now() - started) / HOLD_MS) * 100);
      setProgress(pct);
      if (pct >= 100) advance();
    }, 100);
    return clearTimer;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i, playing]);

  useEffect(() => { if (audioRef.current) audioRef.current.muted = muted; }, [muted]);
  useEffect(() => () => clearTimer(), []);

  if (!slides.length) return null;

  const toggle = () => {
    const a = audioRef.current;
    if (playing) { a?.pause(); setPlaying(false); return; }
    if (i === slides.length - 1 && progress >= 100) { goTo(0, true); setPlaying(true); return; }
    setPlaying(true);
    if (slide.audio && a) a.play().catch(() => {});
  };

  return (
    <section
      className={`mb-5 rounded-xl border border-border bg-[#0c0f14] overflow-hidden ${compact ? "" : "shadow-lg"}`}
      data-testid={`kb-walkthrough-${slug}`}
      aria-label="Narrated walkthrough"
    >
      <div className="relative aspect-video bg-black">
        <img
          key={slide.image}
          src={slide.image}
          alt={slide.title}
          className="absolute inset-0 w-full h-full object-contain animate-in fade-in duration-300"
          data-testid="kb-walkthrough-slide-image"
        />
        {captions && !compact && (
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/60 to-transparent px-4 pt-10 pb-3">
            <p className="text-[10px] uppercase tracking-wider text-primary font-semibold mb-0.5" data-testid="kb-walkthrough-slide-title">
              {i + 1} / {slides.length} · {slide.title}
            </p>
            <p className="text-xs sm:text-sm text-white/90 leading-snug" data-testid="kb-walkthrough-caption">{slide.narration}</p>
          </div>
        )}
        {!playing && (
          <button
            type="button"
            onClick={toggle}
            className="absolute inset-0 m-auto h-14 w-14 rounded-full bg-primary text-primary-foreground grid place-items-center shadow-xl hover:scale-105 transition-transform"
            aria-label="Play walkthrough"
            data-testid="kb-walkthrough-play-overlay"
          >
            <Play size={26} weight="fill" />
          </button>
        )}
      </div>
      <div className="h-1 bg-white/10">
        <div className="h-full bg-primary transition-[width] duration-100" style={{ width: `${progress}%` }} data-testid="kb-walkthrough-progress" />
      </div>
      {captions && compact && (
        <div className="px-3 pt-2">
          <p className="text-[10px] uppercase tracking-wider text-primary font-semibold mb-0.5" data-testid="kb-walkthrough-slide-title">
            {i + 1} / {slides.length} · {slide.title}
          </p>
          <p className="text-xs text-white/85 leading-snug" data-testid="kb-walkthrough-caption">{slide.narration}</p>
        </div>
      )}
      <div className="flex items-center gap-1 px-2 py-1.5">
        <button type="button" onClick={() => goTo(i - 1)} disabled={i === 0} className="p-1.5 rounded hover:bg-white/10 disabled:opacity-30 text-white" aria-label="Previous slide" data-testid="kb-walkthrough-prev"><SkipBack size={16} weight="fill" /></button>
        <button type="button" onClick={toggle} className="p-1.5 rounded hover:bg-white/10 text-white" aria-label={playing ? "Pause" : "Play"} data-testid="kb-walkthrough-toggle">
          {playing ? <Pause size={16} weight="fill" /> : <Play size={16} weight="fill" />}
        </button>
        <button type="button" onClick={() => goTo(i + 1)} disabled={i === slides.length - 1} className="p-1.5 rounded hover:bg-white/10 disabled:opacity-30 text-white" aria-label="Next slide" data-testid="kb-walkthrough-next"><SkipForward size={16} weight="fill" /></button>
        <div className="flex-1 flex items-center gap-1 px-2">
          {slides.map((s, n) => (
            <button key={s.image + n} type="button" onClick={() => goTo(n)} aria-label={`Slide ${n + 1}: ${s.title}`}
              className={`h-1.5 flex-1 rounded-full transition-colors ${n === i ? "bg-primary" : n < i ? "bg-primary/40" : "bg-white/15 hover:bg-white/30"}`}
              data-testid={`kb-walkthrough-dot-${n + 1}`} />
          ))}
        </div>
        {liveTour && (
          <button type="button" onClick={() => { audioRef.current?.pause(); setPlaying(false); launchLive(liveTour); }} className="hidden sm:inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-primary border border-primary/40 rounded-full px-2 py-0.5 mr-1 hover:bg-primary/10" data-testid="kb-walkthrough-live-tour">
            <CursorClick size={11} weight="fill" /> Try it live
          </button>
        )}
        <span className="text-[10px] text-white/50 hidden sm:inline mr-1" data-testid="kb-walkthrough-kind">{narrated ? "Narrated walkthrough" : "Walkthrough"}</span>
        <button type="button" onClick={() => setCaptions(c => !c)} className={`p-1.5 rounded hover:bg-white/10 ${captions ? "text-primary" : "text-white/60"}`} aria-label="Toggle captions" data-testid="kb-walkthrough-captions"><ClosedCaptioning size={16} weight={captions ? "fill" : "regular"} /></button>
        {narrated && (
          <button type="button" onClick={() => setMuted(m => !m)} className="p-1.5 rounded hover:bg-white/10 text-white" aria-label={muted ? "Unmute" : "Mute"} data-testid="kb-walkthrough-mute">
            {muted ? <SpeakerSlash size={16} /> : <SpeakerHigh size={16} />}
          </button>
        )}
      </div>
      <audio ref={audioRef} preload="auto" onEnded={advance} onTimeUpdate={(e) => { const a = e.currentTarget; if (a.duration) setProgress((a.currentTime / a.duration) * 100); }} data-testid="kb-walkthrough-audio" />
    </section>
  );
}
